import { useState } from "react";
import type { AppLocale } from "@akina/infra";
import { DateInput, Icon } from "../atoms";
import { DatePicker } from "./DatePicker/DatePicker";
import { useLocaleStore } from "../../hooks";

export const MIN_DATE_OF_BIRTH_YEAR = 1900;
export const DATE_OF_BIRTH_FORMAT_LENGTH = 10;

export const formatDate = (raw: string): string => {
  const digits = raw.replaceAll(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

export const toIsoDate = (ddmmyyyy: string): string | null => {
  const digits = ddmmyyyy.replaceAll(/\D/g, "");
  if (digits.length !== 8) return null;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4);
  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);
  const d = new Date(yearNum, monthNum - 1, dayNum);
  if (
    Number.isNaN(d.getTime()) ||
    d.getDate() !== dayNum ||
    d.getMonth() + 1 !== monthNum ||
    d.getFullYear() !== yearNum ||
    d.getFullYear() < MIN_DATE_OF_BIRTH_YEAR
  ) return null;
  return `${year}-${month}-${day}`;
};

export const toDdmmyyyy = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

type DateOfBirthInputProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  name?: string;
  invalid?: boolean;
  locale?: AppLocale;
  searchPlaceholder?: string;
  className?: string;
  inputTestId?: string;
}>;

export function DateOfBirthInput({
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  name,
  invalid,
  locale: localeProp,
  searchPlaceholder,
  className,
  inputTestId,
}: DateOfBirthInputProps) {
  const storeLocale = useLocaleStore(s => s.locale);
  const locale = localeProp ?? storeLocale;

  const [text, setText] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setText(value);
  }

  const handleChange = (raw: string) => {
    const formatted = formatDate(raw);
    setText(formatted);
    onChange(formatted);
  };

  const pickerDate: Date | undefined = (() => {
    const iso = toIsoDate(text);
    return iso ? new Date(iso) : undefined;
  })();

  return (
    <div className={className}>
      <DateInput
        name={name}
        value={text}
        onPaste={e => {
          e.preventDefault();
          handleChange(e.clipboardData.getData("text/plain"));
        }}
        onChange={e => handleChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        min={`${MIN_DATE_OF_BIRTH_YEAR}-01-01`}
        max="2050-12-31"
        label={label}
        error={invalid ? " " : undefined}
        datePicker={
          <DatePicker
            date={pickerDate}
            onChange={(d: Date) => handleChange(toDdmmyyyy(d))}
            trigger={
              <button type="button" className="group">
                <Icon type="calendar" className="group-hover:text-primary-100 text-gray-800/30" sizeClasses="h-4 w-4" />
              </button>
            }
            locale={locale}
            searchPlaceholder={searchPlaceholder}
          />
        }
        inputProps={inputTestId ? { "data-testid": inputTestId } : undefined}
      />
    </div>
  );
}
